/**
 * Resource/workspace image utilities
 * Provides placeholder images of modern workspaces when resource images are not available.
 * Uses local office images from public/placeholders/office/ (downloaded from Unsplash).
 * Run `node scripts/download-office-images.js` to refresh images.
 */

const IMAGE_SIZE = 150
const OFFICE_IMAGE_COUNT = 30
const OFFICE_IMAGES_PATH = '/placeholders/office'

/**
 * Hash a string to a numeric index.
 * @param {string} str - Input string
 * @returns {number} Numeric hash
 */
function hashToIndex(str) {
  let hash = 0
  const s = String(str).trim()
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * Generate a placeholder workspace image URL.
 * Uses local office images from public/placeholders/office/.
 * @param {string} seed - Unique identifier (resource name, id, etc.)
 * @param {number} [size=150] - Image size in pixels (ignored for local images)
 * @returns {string} Image URL
 */
export function getPlaceholderResourceImageUrl(seed, size = IMAGE_SIZE) {
  if (!seed || String(seed).trim() === '') {
    return null
  }
  const s = String(seed).trim()
  const idx = (hashToIndex(s) % OFFICE_IMAGE_COUNT) + 1
  return `${OFFICE_IMAGES_PATH}/office-${String(idx).padStart(2, '0')}.jpg`
}

/**
 * Get resource image URL - returns real URL if provided, otherwise workspace placeholder.
 * @param {string|null} realUrl - Image URL from API.
 * @param {string} seed - Fallback seed (resource name, id) for placeholder
 * @param {number} [size=150] - Size for generated placeholder
 * @returns {string|null} Image URL
 */
export function getResourceImageUrl(realUrl, seed, size = IMAGE_SIZE) {
  if (realUrl && typeof realUrl === 'string' && realUrl.trim() !== '') {
    return realUrl
  }
  return getPlaceholderResourceImageUrl(seed, size)
}
